import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Interface for JWT token payload structure
 */
interface JwtPayload {
  sub?: string;
  exp?: number;
  iat?: number;
  is_beta_user?: string | boolean;
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class BetaService {
  private isBetaUserSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isBetaUser$: Observable<boolean> = this.isBetaUserSubject.asObservable();

  // Configuration: List of modules that are currently in beta
  // Add or remove module route links here to control beta access
  private readonly BETA_MODULES: string[] = ['freeform-collection'];

  constructor() {
    this.checkBetaUserStatus();
  }

  /**
   * Decodes JWT token and extracts the payload
   */
  private decodeJwtToken(token: string): JwtPayload | null {
    try {
      const base64Url = token.split('.')[1];
      if (!base64Url) {
        return null;
      }
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      return null;
    }
  }

  /**
   * Checks if the current user is a beta user by decoding the id_token
   */
  private checkBetaUserStatus(): void {
    // Use id_token instead of access_token, as is_beta_user is in the id_token payload
    const idToken = localStorage.getItem('idToken');
    if (!idToken) {
      this.isBetaUserSubject.next(false);
      return;
    }

    const payload = this.decodeJwtToken(idToken);
    if (!payload) {
      this.isBetaUserSubject.next(false);
      return;
    }

    // is_beta_user is stored as a string "true" or "false"
    // Explicitly check for 'true' (string) or true (boolean)
    // Everything else (including 'false', undefined, null) should be treated as false
    const isBetaUserValue = payload.is_beta_user;
    const isBetaUser = isBetaUserValue === 'true' || isBetaUserValue === true;

    this.isBetaUserSubject.next(isBetaUser);
  }

  /**
   * Gets the current beta user status
   */
  public getIsBetaUser(): boolean {
    return this.isBetaUserSubject.value;
  }

  /**
   * Checks if a module is in the beta list
   */
  public isBetaModule(routeLink: string): boolean {
    return this.BETA_MODULES.includes(routeLink);
  }

  /**
   * Checks if user has access to a module (either not beta or user is beta)
   */
  public hasAccessToModule(routeLink: string): boolean {
    if (!this.isBetaModule(routeLink)) {
      return true; // Non-beta modules are accessible to everyone
    }
    return this.getIsBetaUser(); // Beta modules only accessible to beta users
  }

  /**
   * Refreshes the beta user status (useful after login)
   */
  public refreshBetaUserStatus(): void {
    this.checkBetaUserStatus();
  }

  /**
   * Gets the current user ID from JWT token sub claim
   */
  public getCurrentUserId(): string | null {
    const idToken = localStorage.getItem('idToken');
    if (!idToken) {
      return null;
    }

    const payload = this.decodeJwtToken(idToken);
    if (!payload) {
      return null;
    }

    return payload.sub || null;
  }

  /**
   * Gets the list of beta modules (for reference)
   */
  public getBetaModules(): string[] {
    return [...this.BETA_MODULES];
  }
}

